import React, { Suspense, lazy, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useRestaurant } from "../../../contexts/RestaurantContext";
import {
    selectIsCustomerLoggedIn,
    selectCustomerUser,
    customerLogout,
} from "../../../app/customerAuthSlice";
import {
    fetchCustomerUnreadCount,
    selectCustomerUnreadNotifications,
} from "../../../app/customerNotificationSlice";
import { clearAllCustomerInfoOnLogout, selectCart, updateCustomerInfo } from "../../../app/cartSlice";
import {
    setCartOpen,
    setCollectionTimeModalOpen,
    selectCollectionTimeModalOpen,
    setPendingCollectionMenuEntry,
    selectPendingCollectionMenuEntry,
    setBrowseStartOrderModalOpen,
    selectBrowseStartOrderModalOpen,
    clearPendingBrowseProduct,
    selectGlobalSearchQuery,
    setGlobalSearchQuery,
    clearGlobalSearchQuery,
} from "../../../app/onlineOrderingUiSlice";
import GlobalOrderingSearchBar from "../GlobalOrderingSearchBar/GlobalOrderingSearchBar";
import "../GlobalOrderingSearchBar/GlobalOrderingSearch.css";
import { isCollectionOrderType } from "../../../utils/collectionTimeSlots";
import { resolveHoursStringForOrderType } from "../../../utils/onlineOrderingHours";
import {
    isBrowseOrderType,
    browseModeOrderHint,
    shouldOpenBrowseStartOrderModal,
} from "../../../utils/orderTypeHelpers";
import { showInfoToast } from "../../CustomComponents/toast/toast";
import CollectionTimeBanner from "../CollectionTimeBanner/CollectionTimeBanner";
import { selectCurrency, selectRestaurantData, selectCompanySettings } from "../../../app/restaurantSlice";
import {
    isOnlineOrderingAllowed,
    isReservationAllowed,
} from "../../../utils/companyFeatureFlags";
import OnlineOrderingPromo from "../OnlineOrderingPromo/OnlineOrderingPromo";
import useOnlineOrderingPromos from "../../../hooks/useOnlineOrderingPromos";
import LoginModal from "../LoginModal/LoginModal";
import OnlineOrderingModal from "../OnlineOrderingModal/OnlineOrderingModal";
import {
    getRestaurantDisplayName,
    getResolvedCoverSrc,
    getRestaurantLogoCandidates,
    RESTAURANT_LOGO_PLACEHOLDER,
} from "../MenuPageHero/menuHeroUtils";
import { scrollRestaurantAppToTopAfterNav } from "../../../utils/scrollRestaurantAppToTop";
import useMobileViewportChromeFix from "../../../hooks/useMobileViewportChromeFix";
import "./RestaurantSiteHeader.css";

const CollectionTimeModal = lazy(() =>
    import("../CollectionTimeModal/CollectionTimeModal").then((m) => ({
        default: m.CollectionTimeModal || m.default,
    }))
);
const BrowseStartOrderModal = lazy(() =>
    import("../BrowseStartOrderModal/BrowseStartOrderModal")
);

const HERO_COVER_FALLBACK = "/assets/img/restaurant-image.png";

/**
 * Site chrome shared across restaurant routes: green header + hero banner (matches Home).
 * @param {boolean} chromeLoading - Bootstrap in progress; show skeletons, no placeholder copy/images.
 * @param {boolean} chromeReady - Restaurant data loaded; show real branding and hero.
 */
export default function RestaurantSiteHeader({ chromeLoading = false, chromeReady = false }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { restaurantSlug, locationId } = useRestaurant();
    const userMenuRef = useRef(null);
    const chromeRef = useRef(null);
    const headerRef = useRef(null);

    useMobileViewportChromeFix();

    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
    const [headerSearchModalOpen, setHeaderSearchModalOpen] = useState(false);
    const [headerSearchDraft, setHeaderSearchDraft] = useState("");
    const headerSearchModalInputRef = useRef(null);

    const restaurantData = useSelector((state) =>
        restaurantSlug ? selectRestaurantData(state, restaurantSlug) : null
    );
    const companySettings = useSelector((state) =>
        restaurantSlug ? selectCompanySettings(state, restaurantSlug) : null
    );
    const onlineOrderingEnabled = isOnlineOrderingAllowed(restaurantData, companySettings);
    const reservationEnabled = isReservationAllowed(restaurantData, companySettings);
    const isCustomerLoggedIn = useSelector(selectIsCustomerLoggedIn);
    const customerUser = useSelector(selectCustomerUser);
    const customerDisplayName = (customerUser?.name ?? customerUser?.Cust_Name ?? "").trim();
    const cart = useSelector((state) => (restaurantSlug ? selectCart(state, restaurantSlug) : null));
    const collectionTimeModalOpen = useSelector((state) =>
        restaurantSlug ? selectCollectionTimeModalOpen(state, restaurantSlug) : false
    );
    const pendingCollectionMenuEntry = useSelector((state) =>
        restaurantSlug ? selectPendingCollectionMenuEntry(state, restaurantSlug) : false
    );
    const browseStartOrderModalOpen = useSelector((state) =>
        restaurantSlug ? selectBrowseStartOrderModalOpen(state, restaurantSlug) : false
    );
    const unreadNotificationsCount = useSelector(selectCustomerUnreadNotifications);
    const orderType = cart?.orderType || "pickup";
    const customerInfo = cart?.customerInfo || {};
    const cartLocationId = cart?.customerInfo?.locationId;
    const effectiveLocationId =
        cartLocationId === undefined ? locationId ?? null : cartLocationId;

    const cartItemCount = useMemo(() => {
        if (!Array.isArray(cart?.items)) return 0;
        return cart.items.reduce((total, item) => total + (item?.quantity || 1), 0);
    }, [cart?.items]);

    const restaurantDisplayName = useMemo(() => {
        if (!chromeReady) return "";
        const info = restaurantData && typeof restaurantData === "object" ? restaurantData : {};
        return getRestaurantDisplayName(info) || restaurantSlug || "";
    }, [restaurantData, restaurantSlug, chromeReady]);

    const heroCoverUrl = useMemo(() => {
        if (!chromeReady) return "";
        const info = restaurantData && typeof restaurantData === "object" ? restaurantData : {};
        return getResolvedCoverSrc(info, HERO_COVER_FALLBACK);
    }, [restaurantData, chromeReady]);

    const brandLogoCandidates = useMemo(() => {
        if (!chromeReady) return [];
        const info = restaurantData && typeof restaurantData === "object" ? restaurantData : {};
        return getRestaurantLogoCandidates(info);
    }, [restaurantData, chromeReady]);

    const [brandLogoCandidateIndex, setBrandLogoCandidateIndex] = useState(0);
    const brandLogoCandidatesKey = brandLogoCandidates.join("|");

    useEffect(() => {
        setBrandLogoCandidateIndex(0);
    }, [brandLogoCandidatesKey]);

    const brandLogoSrc =
        brandLogoCandidates[brandLogoCandidateIndex] || RESTAURANT_LOGO_PLACEHOLDER;

    useEffect(() => {
        if (!isCustomerLoggedIn || !restaurantSlug) return;
        dispatch(fetchCustomerUnreadCount({ restaurantSlug, locationId: effectiveLocationId }));
    }, [dispatch, isCustomerLoggedIn, restaurantSlug, effectiveLocationId]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, []);

    const slugPrefix = restaurantSlug ? `/${restaurantSlug}/` : "";
    const onOrderingPage =
        !!restaurantSlug &&
        !!slugPrefix &&
        (location.pathname.startsWith(`${slugPrefix}menu`) ||
            location.pathname.startsWith(`${slugPrefix}combos`) ||
            location.pathname.startsWith(`${slugPrefix}deals`));
    const headerSearchQuery = useSelector((state) =>
        restaurantSlug ? selectGlobalSearchQuery(state, restaurantSlug) : ""
    );
    const showHeaderSearch = onOrderingPage && !chromeLoading;

    useEffect(() => {
        if (!showHeaderSearch) {
            setHeaderSearchModalOpen(false);
        }
    }, [showHeaderSearch, location.pathname]);

    const openHeaderSearchModal = () => {
        // Draft only — Redux/query (and menu filter) update on submit, not while typing.
        setHeaderSearchDraft(headerSearchQuery || "");
        setHeaderSearchModalOpen(true);
    };

    const clearHeaderSearch = () => {
        setHeaderSearchDraft("");
        if (restaurantSlug) {
            dispatch(clearGlobalSearchQuery({ restaurantSlug }));
        }
        setHeaderSearchModalOpen(false);
    };

    const hasActiveHeaderSearch = Boolean(String(headerSearchQuery || "").trim());

    useEffect(() => {
        if (!headerSearchModalOpen) return undefined;
        const frame = requestAnimationFrame(() => {
            headerSearchModalInputRef.current?.querySelector?.("input")?.focus?.();
        });
        return () => cancelAnimationFrame(frame);
    }, [headerSearchModalOpen]);

    useEffect(() => {
        if (!restaurantSlug || !onOrderingPage) return;
        if (searchParams.get("scheduleCollection") !== "1") return;
        const isPickup = orderType === "pickup" || orderType === "collection";
        if (!isPickup) return;
        dispatch(setCollectionTimeModalOpen({ restaurantSlug, isOpen: true }));
    }, [restaurantSlug, searchParams, orderType, onOrderingPage, dispatch]);

    const isHomeRoute = /\/home\/?$/i.test(location.pathname);
    const isReservationRoute = /\/reservation\/?$/i.test(location.pathname);
    const hidePromoRoutes =
        /\/(checkout|reservation|my-orders|track-order|profile|notifications|reset-password|table|order-details)(\/|$)/i.test(
            location.pathname
        ) || /\/(checkout|reservation)\/complete/i.test(location.pathname);
    const showPromoSlot = Boolean(
        restaurantSlug && onlineOrderingEnabled && !hidePromoRoutes
    );
    const {
        banners: promoBanners,
        offers: promoOffers,
        loaded: promosLoaded,
    } = useOnlineOrderingPromos({
        enabled: showPromoSlot,
    });
    const hasBanners = promoBanners.length > 0;
    const hasOffers = promoOffers.length > 0;
    const hasPromoContent = hasBanners || hasOffers;
    const promoVariant = isHomeRoute || onOrderingPage ? "hero" : "strip";
    // Cover hero: reservation when allowed; ordering pages / home when OO allowed and no banners.
    const showRestaurantHero =
        (isReservationRoute && reservationEnabled) ||
        (onlineOrderingEnabled &&
            (isHomeRoute || onOrderingPage) &&
            promosLoaded &&
            !hasBanners);
    const currency = useSelector((state) =>
        restaurantSlug ? selectCurrency(state, restaurantSlug) : "£"
    );

	const showCollectionInHeader = onOrderingPage && isCollectionOrderType(orderType);

	const collectionOpeningHours = resolveHoursStringForOrderType(
		"collection",
		restaurantData,
		restaurantData?.openingHours || ""
	);

    useLayoutEffect(() => {
        if (!restaurantSlug || !isHomeRoute) return;
        scrollRestaurantAppToTopAfterNav();
    }, [restaurantSlug, isHomeRoute, location.pathname, location.key]);

    useLayoutEffect(() => {
        const header = headerRef.current;
        const chrome = chromeRef.current;
        if (!header || !chrome) return undefined;

        const syncHeaderOffset = () => {
            const height = Math.ceil(header.getBoundingClientRect().height);
            const offset = `${height}px`;
            chrome.style.setProperty("--restaurant-site-mobile-header-offset", offset);
            document.documentElement.style.setProperty("--restaurant-site-mobile-header-offset", offset);
        };

        syncHeaderOffset();

        const resizeObserver =
            typeof ResizeObserver !== "undefined" ? new ResizeObserver(syncHeaderOffset) : null;
        resizeObserver?.observe(header);
        window.addEventListener("orientationchange", syncHeaderOffset);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener("orientationchange", syncHeaderOffset);
        };
    }, [chromeLoading, chromeReady, showCollectionInHeader, customerDisplayName]);

    if (!restaurantSlug) {
        return null;
    }

    const goHome = () => {
        if (!restaurantSlug) return;
        navigate(`/${restaurantSlug}/home`);
        scrollRestaurantAppToTopAfterNav();
    };

    const clearScheduleCollectionFromUrl = () => {
        if (searchParams.get("scheduleCollection") !== "1") return;
        const next = new URLSearchParams(searchParams);
        next.delete("scheduleCollection");
        setSearchParams(next, { replace: true });
    };

    const openCollectionTimeModal = () => {
        if (!restaurantSlug) return;
        dispatch(setCollectionTimeModalOpen({ restaurantSlug, isOpen: true }));
    };

    const closeCollectionTimeModal = () => {
        if (!restaurantSlug) return;
        const cameFromScheduleFlow = searchParams.get("scheduleCollection") === "1";
        const hasPickupTime = !!(customerInfo?.pickupTime || "").trim();
        const wasPendingMenuEntry = pendingCollectionMenuEntry;
        dispatch(setPendingCollectionMenuEntry({ restaurantSlug, pending: false }));
        dispatch(setCollectionTimeModalOpen({ restaurantSlug, isOpen: false }));
        clearScheduleCollectionFromUrl();
        // Dismiss without a time: stay/return home (never leave menu open without pickup time).
        if (wasPendingMenuEntry) {
            if (!isHomeRoute) {
                navigate(`/${restaurantSlug}/home`, { replace: true });
                scrollRestaurantAppToTopAfterNav();
            }
            return;
        }
        if (cameFromScheduleFlow || (onOrderingPage && !hasPickupTime)) {
            navigate(`/${restaurantSlug}/home`, { replace: true });
            scrollRestaurantAppToTopAfterNav();
        }
    };

    const handleCollectionTimeConfirm = ({ pickupTime, pickupDate }) => {
        if (!restaurantSlug) return;
        const shouldEnterMenu = pendingCollectionMenuEntry;
        dispatch(
            updateCustomerInfo({
                restaurantSlug,
                customerInfo: {
                    ...customerInfo,
                    pickupTime,
                    pickupDate,
                },
            })
        );
        dispatch(setPendingCollectionMenuEntry({ restaurantSlug, pending: false }));
        dispatch(setCollectionTimeModalOpen({ restaurantSlug, isOpen: false }));
        clearScheduleCollectionFromUrl();
        // Collect from home: open menu only after a time is confirmed.
        if (shouldEnterMenu) {
            navigate(`/${restaurantSlug}/menu`);
        }
    };

    const requestLogout = () => {
        setShowUserMenu(false);
        setLogoutConfirmOpen(true);
    };

    const confirmLogout = () => {
        setLogoutConfirmOpen(false);
        dispatch(clearAllCustomerInfoOnLogout());
        dispatch(customerLogout({ restaurantSlug }));
    };

    const closeBrowseStartOrderModal = () => {
        if (!restaurantSlug) return;
        // Dismiss without choosing a channel — drop any product waiting to be added.
        dispatch(clearPendingBrowseProduct({ restaurantSlug }));
        dispatch(setBrowseStartOrderModalOpen({ restaurantSlug, isOpen: false }));
    };

    const finishBrowseStartOrderModal = () => {
        if (!restaurantSlug) return;
        // Keep pendingBrowseProduct so Menu can resume add-to-cart after Delivery/Collection.
        dispatch(setBrowseStartOrderModalOpen({ restaurantSlug, isOpen: false }));
    };

    const openCart = () => {
        if (!restaurantSlug) return;
        if (isBrowseOrderType(orderType)) {
            if (shouldOpenBrowseStartOrderModal(orderType, customerInfo)) {
                dispatch(setBrowseStartOrderModalOpen({ restaurantSlug, isOpen: true }));
                return;
            }
            showInfoToast(browseModeOrderHint(customerInfo));
            return;
        }
        const path = location.pathname;

        if (onOrderingPage) {
            if (typeof window !== "undefined" && window.matchMedia("(min-width: 969px)").matches) {
                document.getElementById("ordering-page-basket")?.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                });
                return;
            }
            dispatch(setCartOpen({ restaurantSlug, isCartOpen: true }));
            return;
        }

        const onCheckoutPage =
            !!slugPrefix && path.startsWith(`${slugPrefix}checkout`) && !path.includes("/checkout/");

        if (onCheckoutPage) {
            document.querySelector(".checkout-card--summary")?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
            return;
        }

        const returnTo = `${location.pathname}${location.search}`;
        navigate(`/${restaurantSlug}/cart`, { state: { returnTo } });
    };

    return (
        <div className="restaurant-site-chrome" ref={chromeRef}>
            <header ref={headerRef} className="restaurant-site-header home-ph-header shadow-sm">
                <div className="container-xxl py-2 py-md-3">
                    <div
                        className={`restaurant-site-header__toolbar d-flex align-items-center justify-content-between gap-2 gap-sm-3 min-w-0 ${showCollectionInHeader ? "restaurant-site-header__toolbar--with-collection" : "flex-nowrap"}`}
                    >
                        <button
                            type="button"
                            className="restaurant-site-header__brand-row restaurant-site-header__brand-row--clickable d-flex align-items-center gap-2 gap-sm-3 min-w-0"
                            onClick={goHome}
                            aria-label={
                                restaurantDisplayName
                                    ? `Go to ${restaurantDisplayName} home`
                                    : "Go to restaurant home"
                            }
                            disabled={chromeLoading}
                        >
                            <div
                                className={`restaurant-site-header__compicon-wrap flex-shrink-0${
                                    chromeLoading ? " restaurant-site-header__skeleton" : ""
                                }`}
                            >
                                {chromeReady && brandLogoSrc ? (
                                    <img
                                        key={brandLogoSrc}
                                        src={brandLogoSrc}
                                        alt=""
                                        width={40}
                                        height={40}
                                        decoding="async"
                                        className="restaurant-site-header__compicon"
                                        onError={() => {
                                            setBrandLogoCandidateIndex((idx) => {
                                                if (idx < brandLogoCandidates.length - 1) {
                                                    return idx + 1;
                                                }
                                                return brandLogoCandidates.length;
                                            });
                                        }}
                                    />
                                ) : null}
                            </div>
                            <div className="restaurant-site-header__brand-stack">
                                {chromeLoading ? (
                                    <>
                                        <span
                                            className="restaurant-site-header__skeleton restaurant-site-header__skeleton--brand"
                                            aria-hidden
                                        />
                                        {isCustomerLoggedIn && customerDisplayName ? (
                                            <span className="restaurant-site-header__greet text-white text-truncate">
                                                Hi, {customerDisplayName}
                                            </span>
                                        ) : null}
                                    </>
                                ) : chromeReady && restaurantDisplayName ? (
                                    <>
                                        <span className="restaurant-site-header__brand text-white fw-bold text-truncate">
                                            {restaurantDisplayName}
                                        </span>
                                        {isCustomerLoggedIn && customerDisplayName ? (
                                            <span className="restaurant-site-header__greet text-white text-truncate">
                                                Hi, {customerDisplayName}
                                            </span>
                                        ) : null}
                                    </>
                                ) : isCustomerLoggedIn && customerDisplayName ? (
                                    <span className="restaurant-site-header__greet text-white text-truncate">
                                        Hi, {customerDisplayName}
                                    </span>
                                ) : null}
                            </div>
                        </button>
                        {showCollectionInHeader ? (
                            <div className="restaurant-site-header__collection-slot restaurant-site-header__collection-slot--inline">
                                <CollectionTimeBanner
                                    orderType={orderType}
                                    pickupTime={customerInfo.pickupTime}
                                    pickupDate={customerInfo.pickupDate}
                                    openingHoursString={collectionOpeningHours}
                                    onChangeClick={openCollectionTimeModal}
                                    variant="header"
                                    compact
                                />
                            </div>
                        ) : null}
                        <div className="home-top-auth-bar home-top-auth-bar--ph flex-shrink-0">
                            {showHeaderSearch ? (
                                <div className="restaurant-site-header__search-controls">
                                    <button
                                        type="button"
                                        className={`home-auth-btn home-auth-btn-search restaurant-site-header__search-toggle${
                                            hasActiveHeaderSearch
                                                ? " restaurant-site-header__search-toggle--active"
                                                : ""
                                        }`}
                                        onClick={openHeaderSearchModal}
                                        title="Search menu"
                                        aria-label="Search menu"
                                        aria-haspopup="dialog"
                                        aria-expanded={headerSearchModalOpen}
                                    >
                                        <svg
                                            className="home-auth-btn-icon"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            aria-hidden="true"
                                        >
                                            <path
                                                d="M21 21l-4.35-4.35"
                                                stroke="currentColor"
                                                strokeWidth="1.8"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <circle
                                                cx="11"
                                                cy="11"
                                                r="7"
                                                stroke="currentColor"
                                                strokeWidth="1.8"
                                            />
                                        </svg>
                                    </button>
                                    {hasActiveHeaderSearch ? (
                                        <button
                                            type="button"
                                            className="home-auth-btn restaurant-site-header__search-clear"
                                            onClick={clearHeaderSearch}
                                            title="Clear search"
                                            aria-label="Clear search"
                                        >
                                            <span aria-hidden="true">×</span>
                                        </button>
                                    ) : null}
                                </div>
                            ) : null}
                            {!isHomeRoute ? (
                                <button
                                    type="button"
                                    className="home-auth-btn home-auth-btn-home home-auth-btn--icon-label"
                                    onClick={goHome}
                                    title="Home"
                                    aria-label="Go to home"
                                >
                                    <svg
                                        className="home-auth-btn-icon"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5Z"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <span className="home-auth-btn-label">Home</span>
                                </button>
                            ) : null}
                            {isCustomerLoggedIn ? (
                                <>
                                    <button
                                        type="button"
                                        className="home-auth-btn home-auth-btn-notification home-auth-btn--icon-label"
                                        onClick={() => {
                                            const q =
                                                effectiveLocationId != null && effectiveLocationId !== ""
                                                    ? `?locationId=${encodeURIComponent(effectiveLocationId)}`
                                                    : "";
                                            navigate(`/${restaurantSlug}/notifications${q}`);
                                        }}
                                        title="Notifications"
                                        aria-label="Notifications"
                                    >
                                        <svg
                                            className="home-auth-btn-icon home-auth-btn-icon-bell"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            aria-hidden
                                        >
                                            <path
                                                d="M12 2C8.5 2 5.5 5 5.5 8.5V14L3.5 16V17H20.5V16L18.5 14V8.5C18.5 5 15.5 2 12 2Z"
                                                stroke="currentColor"
                                                strokeWidth="1.6"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                d="M9 20C9.5 21.1 10.6 22 12 22C13.4 22 14.5 21.1 15 20"
                                                stroke="currentColor"
                                                strokeWidth="1.6"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                        <span className="home-auth-btn-label">Alerts</span>
                                        {unreadNotificationsCount > 0 && (
                                            <span className="home-notification-badge">
                                                {unreadNotificationsCount > 99 ? "99+" : unreadNotificationsCount}
                                            </span>
                                        )}
                                    </button>
                                    <div className="home-user-menu" ref={userMenuRef}>
                                        <button
                                            type="button"
                                            className="home-auth-btn home-auth-btn-menu home-auth-btn--icon-label"
                                            onClick={() => setShowUserMenu((prev) => !prev)}
                                            title="Profile"
                                            aria-label="Profile"
                                        >
                                            <svg className="home-auth-btn-icon" viewBox="0 0 24 24" aria-hidden="true">
                                                <path
                                                    d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-3.3 0-6 1.8-6 4v1h12v-1c0-2.2-2.7-4-6-4Z"
                                                    fill="currentColor"
                                                />
                                            </svg>
                                            <span className="home-auth-btn-label">Profile</span>
                                        </button>
                                        {!isBrowseOrderType(orderType) && onlineOrderingEnabled ? (
                                        <button
                                            type="button"
                                            className="home-auth-btn home-auth-btn-cart home-auth-btn--icon-label"
                                            onClick={openCart}
                                            title="Cart"
                                            aria-label="Cart"
                                        >
                                            <svg className="home-auth-btn-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <path
                                                    d="M3 4h2l1.4 8.1a1.2 1.2 0 0 0 1.2 1h9.8a1.2 1.2 0 0 0 1.2-1l1-5.1H7"
                                                    stroke="currentColor"
                                                    strokeWidth="1.8"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                                <circle cx="9" cy="18" r="1.4" fill="currentColor" />
                                                <circle cx="17" cy="18" r="1.4" fill="currentColor" />
                                            </svg>
                                            <span className="home-auth-btn-label">Cart</span>
                                            {cartItemCount > 0 && (
                                                <span className="home-cart-count-badge">{cartItemCount}</span>
                                            )}
                                        </button>
                                        ) : null}
                                        {showUserMenu && (
                                            <div className="home-user-menu-dropdown">
                                                <button
                                                    type="button"
                                                    className="home-user-menu-item"
                                                    onClick={() => {
                                                        setShowUserMenu(false);
                                                        navigate(`/${restaurantSlug}/profile`);
                                                    }}
                                                >
                                                    Profile
                                                </button>
                                                {onlineOrderingEnabled ? (
                                                <button
                                                    type="button"
                                                    className="home-user-menu-item"
                                                    onClick={() => {
                                                        setShowUserMenu(false);
                                                        navigate(`/${restaurantSlug}/my-orders`);
                                                    }}
                                                >
                                                    My orders
                                                </button>
                                                ) : null}
                                                {onlineOrderingEnabled ? (
                                                <button
                                                    type="button"
                                                    className="home-user-menu-item"
                                                    onClick={() => {
                                                        setShowUserMenu(false);
                                                        navigate(`/${restaurantSlug}/track-order`);
                                                    }}
                                                >
                                                    Track order
                                                </button>
                                                ) : null}                                                <button
                                                    type="button"
                                                    className="home-user-menu-item home-user-menu-item--logout d-sm-none"
                                                    onClick={requestLogout}
                                                >
                                                    Log out
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className="home-auth-btn home-auth-btn-logout home-auth-btn--icon-label d-none d-sm-inline-flex"
                                        onClick={requestLogout}
                                        title="Logout"
                                        aria-label="Logout"
                                    >
                                        <svg className="home-auth-btn-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                            <path
                                                d="M14 7l5 5-5 5"
                                                stroke="currentColor"
                                                strokeWidth="1.8"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                d="M19 12H10"
                                                stroke="currentColor"
                                                strokeWidth="1.8"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                d="M10 5H8a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h2"
                                                stroke="currentColor"
                                                strokeWidth="1.8"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                        <span className="home-auth-btn-label">Log out</span>
                                    </button>
                                </>
                            ) : (
                                <>
                                    {onlineOrderingEnabled ? (
                                    <button
                                        type="button"
                                        className="home-auth-btn home-auth-btn-text d-none d-md-inline-flex"
                                        onClick={() => {
                                            const q =
                                                effectiveLocationId != null && effectiveLocationId !== ""
                                                    ? `?locationId=${encodeURIComponent(effectiveLocationId)}`
                                                    : "";
                                            navigate(`/${restaurantSlug}/track-order${q}`);
                                        }}
                                        title="Track order"
                                        aria-label="Track order"
                                    >
                                        Track order
                                    </button>
                                    ) : null}
                                    <button
                                        type="button"
                                        className="home-auth-btn home-auth-btn-login home-auth-btn-text"
                                        onClick={() => setLoginModalOpen(true)}
                                        title="Log in / Sign up"
                                        aria-label="Log in or sign up"
                                    >
                                        <span className="home-auth-btn-login-long">Log in / Sign up</span>
                                        <span className="home-auth-btn-login-short">Log in</span>
                                    </button>
                                    {!isBrowseOrderType(orderType) && onlineOrderingEnabled ? (
                                    <button
                                        type="button"
                                        className="home-auth-btn home-auth-btn-cart home-auth-btn--icon-label"
                                        onClick={openCart}
                                        title="Cart"
                                        aria-label="Cart"
                                    >
                                        <svg className="home-auth-btn-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                            <path
                                                d="M3 4h2l1.4 8.1a1.2 1.2 0 0 0 1.2 1h9.8a1.2 1.2 0 0 0 1.2-1l1-5.1H7"
                                                stroke="currentColor"
                                                strokeWidth="1.8"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <circle cx="9" cy="18" r="1.4" fill="currentColor" />
                                            <circle cx="17" cy="18" r="1.4" fill="currentColor" />
                                        </svg>
                                        <span className="home-auth-btn-label">Cart</span>
                                        {cartItemCount > 0 && (
                                            <span className="home-cart-count-badge">{cartItemCount}</span>
                                        )}
                                    </button>
                                    ) : null}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {showRestaurantHero ? (
                <section
                    className={`restaurant-site-hero home-ph-hero position-relative text-white d-flex align-items-center overflow-hidden${
                        chromeLoading ? " restaurant-site-hero--loading" : ""
                    }${isReservationRoute ? " restaurant-site-hero--reservation" : ""}`}
                    style={chromeReady && heroCoverUrl ? { backgroundImage: `url(${heroCoverUrl})` } : undefined}
                    aria-label="Restaurant spotlight"
                    aria-busy={chromeLoading}
                >
                    <div className="home-ph-hero-pattern position-absolute top-0 start-0 w-100 h-100" aria-hidden />
                    <div className="home-ph-hero-overlay position-absolute top-0 start-0 w-100 h-100" aria-hidden />
                    <div className="home-ph-hero-vignette position-absolute top-0 start-0 w-100 h-100" aria-hidden />
                    {/* Hero text card hidden — set to true to restore Order online / restaurant name overlay */}
                    {false && (
                    <div className="container position-relative py-5 my-md-1 home-ph-hero-inner">
                        <div className="row justify-content-center">
                            <div className="col-12 col-lg-10 col-xl-8 text-center">
                                <div className="home-ph-hero-card mx-auto">
                                    {chromeLoading ? (
                                        <div className="restaurant-site-hero__skeleton-stack" aria-hidden>
                                            <span className="restaurant-site-header__skeleton restaurant-site-header__skeleton--kicker" />
                                            <span className="restaurant-site-header__skeleton restaurant-site-header__skeleton--title" />
                                            <span className="restaurant-site-header__skeleton restaurant-site-header__skeleton--lead" />
                                        </div>
                                    ) : chromeReady && restaurantDisplayName ? (
                                        isReservationRoute && reservationEnabled ? (
                                            <>
                                                <p className="home-ph-kicker text-uppercase small fw-semibold mb-2 mb-md-3 opacity-90">
                                                    Reservations
                                                </p>
                                                <h1 className="display-6 fw-bold mb-0 text-white home-ph-hero-title">
                                                    {restaurantDisplayName}
                                                </h1>
                                                <p className="home-ph-hero-lead mt-3 mb-0 mx-auto">
                                                    Choose your date, time, and party size — we&apos;ll take care of
                                                    the rest.
                                                </p>
                                            </>
                                        ) : onlineOrderingEnabled ? (
                                            <>
                                                <p className="home-ph-kicker text-uppercase small fw-semibold mb-2 mb-md-3 opacity-90">
                                                    Order online
                                                </p>
                                                <h1 className="display-6 fw-bold mb-0 text-white home-ph-hero-title">
                                                    {restaurantDisplayName}
                                                </h1>
                                                <p className="home-ph-hero-lead mt-3 mb-0 mx-auto">
                                                    Browse the menu, build your order, and check out in minutes.
                                                </p>
                                            </>
                                        ) : reservationEnabled ? (
                                            <>
                                                <p className="home-ph-kicker text-uppercase small fw-semibold mb-2 mb-md-3 opacity-90">
                                                    Reservations
                                                </p>
                                                <h1 className="display-6 fw-bold mb-0 text-white home-ph-hero-title">
                                                    {restaurantDisplayName}
                                                </h1>
                                                <p className="home-ph-hero-lead mt-3 mb-0 mx-auto">
                                                    Book a table for your visit — we&apos;ll take care of the rest.
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <h1 className="display-6 fw-bold mb-0 text-white home-ph-hero-title">
                                                    {restaurantDisplayName}
                                                </h1>
                                            </>
                                        )
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                    )}
                </section>
            ) : null}

            {showPromoSlot && hasPromoContent ? (
                <OnlineOrderingPromo
                    banners={promoBanners}
                    offers={promoOffers}
                    variant={promoVariant}
                    currency={currency}
                />
            ) : null}

            <LoginModal
                isOpen={loginModalOpen}
                onClose={() => setLoginModalOpen(false)}
                restaurantSlug={restaurantSlug}
                locationId={locationId}
                onLoginSuccess={() => setLoginModalOpen(false)}
            />

            {collectionTimeModalOpen ? (
                <Suspense fallback={null}>
                    <CollectionTimeModal
                        isOpen={collectionTimeModalOpen}
                        onClose={closeCollectionTimeModal}
                        onConfirm={handleCollectionTimeConfirm}
                        openingHoursString={collectionOpeningHours}
                        pickupDate={customerInfo.pickupDate}
                        defaultPickupTime={customerInfo.pickupTime}
                    />
                </Suspense>
            ) : null}

            {browseStartOrderModalOpen ? (
                <Suspense fallback={null}>
                    <BrowseStartOrderModal
                        isOpen={browseStartOrderModalOpen}
                        onClose={closeBrowseStartOrderModal}
                        onOrderStarted={finishBrowseStartOrderModal}
                        restaurantData={restaurantData}
                    />
                </Suspense>
            ) : null}

            <OnlineOrderingModal
                show={headerSearchModalOpen}
                onHide={() => setHeaderSearchModalOpen(false)}
                centered
                className="online-ordering restaurant-header-search-modal"
                dialogClassName="restaurant-header-search-modal__dialog"
                contentClassName="restaurant-header-search-modal__content border-0"
                backdropClassName="restaurant-header-search-modal__backdrop"
            >
                <OnlineOrderingModal.Header
                    closeButton
                    className="restaurant-header-search-modal__header border-0"
                >
                    <OnlineOrderingModal.Title className="restaurant-header-search-modal__title">
                        Search menu
                    </OnlineOrderingModal.Title>
                </OnlineOrderingModal.Header>
                <OnlineOrderingModal.Body className="restaurant-header-search-modal__body">
                    <div ref={headerSearchModalInputRef}>
                        <GlobalOrderingSearchBar
                            value={headerSearchDraft}
                            onChange={(query) => setHeaderSearchDraft(query ?? "")}
                            onClear={clearHeaderSearch}
                            onSubmit={(query) => {
                                if (!restaurantSlug) return;
                                const next = String(query || "").trim();
                                if (next) {
                                    dispatch(
                                        setGlobalSearchQuery({
                                            restaurantSlug,
                                            query: next,
                                        })
                                    );
                                } else {
                                    dispatch(clearGlobalSearchQuery({ restaurantSlug }));
                                }
                                setHeaderSearchModalOpen(false);
                            }}
                            placeholder="Search menu…"
                            ariaLabel="Search menu"
                            className="restaurant-header-search-modal__input"
                        />
                    </div>
                </OnlineOrderingModal.Body>
            </OnlineOrderingModal>

            <OnlineOrderingModal
                show={logoutConfirmOpen}
                onHide={() => setLogoutConfirmOpen(false)}
                centered
                className="online-ordering logout-confirm-modal"
                dialogClassName="logout-confirm-modal__dialog"
                contentClassName="logout-confirm-modal__content border-0"
                backdropClassName="logout-confirm-modal__backdrop"
            >
                <OnlineOrderingModal.Body className="logout-confirm-modal__body p-0">
                    <div className="logout-confirm-modal__inner">
                        <div className="logout-confirm-modal__icon" aria-hidden="true">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M14 7l5 5-5 5M19 12H10M10 5H8a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h2"
                                    stroke="currentColor"
                                    strokeWidth="1.75"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        <h2 className="logout-confirm-modal__title">Sign out?</h2>
                        <p className="logout-confirm-modal__message">
                            Are you sure you want to sign out of your account?
                        </p>
                        <div className="logout-confirm-modal__actions">
                            <button
                                type="button"
                                className="btn logout-confirm-modal__btn logout-confirm-modal__btn--cancel"
                                onClick={() => setLogoutConfirmOpen(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-serveiq text-white logout-confirm-modal__btn logout-confirm-modal__btn--confirm"
                                onClick={confirmLogout}
                            >
                                Sign out
                            </button>
                        </div>
                    </div>
                </OnlineOrderingModal.Body>
            </OnlineOrderingModal>
        </div>
    );
}
